import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Navbar from "../components/Navbar";
import "./css/CommonComplete.css";

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

// Allowed file types
const ALLOWED_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".svg", ".webp", ".avif", ".heic", ".pdf", ".doc", ".docx"];

export default function ProfileStep3() {
  const navigate = useNavigate();
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [declaration1, setDeclaration1] = useState(false);
  const [declaration2, setDeclaration2] = useState(false);
  
  const [touched, setTouched] = useState({});
  const [throwError, setThrowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [deletingId, setDeletingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  
  const fileInputRef = useRef(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      step: "Step",
      companyDocuments: "Company Documents",
      subtitle: "Please add the documents for verification",
      establishmentDocuments: "Establishment Documents",
      maxFilesInfo: "Max 5 documents allowed, upload pdf or jpg only",
      clickToUpload: "Click Here To Upload",
      fileTooLarge: "File size must be less than 5MB",
      invalidFileType: "Invalid file type. Please upload PDF, JPG, JPEG, PNG, SVG, WebP, AVIF, or HEIC files only.",
      maxFilesReached: "Maximum 5 documents allowed. Please remove some files before uploading more.",
      deleteFile: "Delete file",
      viewFile: "View file",
      declaration1: "By continuing, I accept the",
      termsOfService: "terms of service",
      and: "and",
      privacyPolicy: "privacy policy",
      declaration2: "I hereby declare that the information provided is true and correct to the best of my knowledge, and I am authorized to register on behalf of the company.",
      acceptDeclarations: "Please accept both declarations to continue.",
      continue: "Continue",
      failedToLoad: "Failed to load profile data",
      somethingWrong: "Something went wrong!",
      uploadSuccess: "Documents uploaded successfully!",
      uploadFailed: "Failed to upload documents",
      deleteSuccess: "Document deleted successfully!",
      deleteFailed: "Failed to delete document",
      loadingDocument: "Loading document...",
      documentLoadFailed: "Failed to load document",
    },
    ar: {
      loading: "جاري التحميل",
      step: "الخطوة",
      companyDocuments: "مستندات الشركة",
      subtitle: "يرجى إضافة المستندات للتحقق",
      establishmentDocuments: "مستندات التأسيس",
      maxFilesInfo: "الحد الأقصى 5 مستندات، قم بتحميل PDF أو JPG فقط",
      clickToUpload: "انقر هنا للتحميل",
      fileTooLarge: "يجب أن يكون حجم الملف أقل من 5 ميجابايت",
      invalidFileType: "نوع ملف غير صحيح. يرجى تحميل ملفات PDF أو JPG أو JPEG أو PNG أو SVG أو WebP أو AVIF أو HEIC فقط.",
      maxFilesReached: "الحد الأقصى 5 مستندات. يرجى إزالة بعض الملفات قبل تحميل المزيد.",
      deleteFile: "حذف الملف",
      viewFile: "عرض الملف",
      declaration1: "بالمتابعة، أوافق على",
      termsOfService: "شروط الخدمة",
      and: "و",
      privacyPolicy: "سياسة الخصوصية",
      declaration2: "أقر بأن المعلومات المقدمة صحيحة ودقيقة حسب علمي، وأنا مخول للتسجيل نيابة عن الشركة.",
      acceptDeclarations: "يرجى قبول كلا الإعلانين للمتابعة.",
      continue: "متابعة",
      failedToLoad: "فشل تحميل بيانات الملف الشخصي",
      somethingWrong: "حدث خطأ ما!",
      uploadSuccess: "تم تحميل المستندات بنجاح!",
      uploadFailed: "فشل تحميل المستندات",
      deleteSuccess: "تم حذف المستند بنجاح!",
      deleteFailed: "فشل حذف المستند",
      loadingDocument: "جاري تحميل المستند...",
      documentLoadFailed: "فشل تحميل المستند",
    },
  }[lang];

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setInitialLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profile = data.response;
          
          // Set language preference from API response to localStorage
          if (profile.language_preference) {
            localStorage.setItem("app_language", profile.language_preference);
            setLang(profile.language_preference);
          }

          // Navigation logic based on profile_completed_step and is_verified
          const completedStep = profile.profile_completed_step || 0;
          
          // If onboarding is complete, navigate to dashboard
          if (completedStep === 5) {
            navigate("/dashboard");
            return;
          }
          
          // If verified, redirect from steps 1-3 to step 4
          const currentStep = 3;
          if (profile.is_verified === true && currentStep <= 3) {
            navigate("/profile-step-4");
            return;
          }
          
          // If current step is ahead of completed step + 1, redirect to appropriate step
          if (currentStep > completedStep + 1) {
            const nextStep = completedStep + 1;
            navigate(`/profile-step-${nextStep}`);
            return;
          }
          
          // Load existing verification documents
          if (profile.verification_documents && Array.isArray(profile.verification_documents)) {
            setExistingDocuments(profile.verification_documents);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.code !== 404) {
          const currentLang = localStorage.getItem("app_language") || "en";
          const translations = {
            en: { failedToLoad: "Failed to load profile data" },
            ar: { failedToLoad: "فشل تحميل بيانات الملف الشخصي" },
          };
          showToast(err.message || translations[currentLang].failedToLoad, "error");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProfile();
  }, [USER_BASE_URL, VERSION, navigate]);

  // Validate file
  const validateFile = (file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      // Also check by extension as fallback
      const fileName = file.name.toLowerCase();
      const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
      if (!hasValidExtension) {
        return { valid: false, error: t.invalidFileType };
      }
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t.fileTooLarge };
    }

    return { valid: true };
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalFiles = existingDocuments.length + selectedFiles.length;
    
    if (totalFiles + files.length > MAX_FILES) {
      showToast(t.maxFilesReached, "error");
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        showToast(validation.error, "error");
      }
    }

    if (validFiles.length > 0) {
      const newTotal = totalFiles + validFiles.length;
      if (newTotal > MAX_FILES) {
        const remaining = MAX_FILES - totalFiles;
        setSelectedFiles([...selectedFiles, ...validFiles.slice(0, remaining)]);
        showToast(`Only ${remaining} more file(s) can be uploaded.`, "error");
      } else {
        setSelectedFiles([...selectedFiles, ...validFiles]);
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove selected file
  const removeSelectedFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Delete existing document
  const handleDeleteDocument = async (documentId) => {
    if (deletingId || viewingId) return;
    
    try {
      setDeletingId(documentId);
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/complete-profile/delete-verification-document`,
        "POST",
        JSON.stringify({ key: documentId }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        setExistingDocuments(existingDocuments.filter(doc => doc.id !== documentId));
        showToast(t.deleteSuccess, "success");
      } else {
        showToast(data.message || t.deleteFailed, "error");
      }
    } catch (err) {
      console.error("Error deleting document:", err);
      showToast(err.message || t.deleteFailed, "error");
    } finally {
      setDeletingId(null);
    }
  };

  // View existing document
  const handleViewDocument = async (documentId) => {
    if (viewingId || deletingId) return;
    
    try {
      setViewingId(documentId);
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/complete-profile/get-verification-document`,
        "POST",
        JSON.stringify({ key: documentId }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200 && data.response?.signed_url) {
        window.open(data.response.signed_url, "_blank");
      } else {
        showToast(data.message || t.documentLoadFailed, "error");
      }
    } catch (err) {
      console.error("Error loading document:", err);
      showToast(err.message || t.documentLoadFailed, "error");
    } finally {
      setViewingId(null);
    }
  };

  // Get file name from URL or file object
  const getFileName = (fileOrDoc) => {
    if (fileOrDoc instanceof File) {
      return fileOrDoc.name;
    }
    if (fileOrDoc.url) {
      const urlParts = fileOrDoc.url.split("/");
      return urlParts[urlParts.length - 1] || "document";
    }
    return "document";
  };

  // Get file extension for icon
  const getFileExtension = (fileOrDoc) => {
    const fileName = getFileName(fileOrDoc).toLowerCase();
    if (fileName.endsWith(".pdf")) return "pdf";
    if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) return "jpg";
    if (fileName.endsWith(".png")) return "png";
    if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) return "doc";
    return "file";
  };

  // Validate inputs
  const validateInputs = () => {
    if (!declaration1 || !declaration2) {
      setThrowError(true);
      return false;
    }
    return true;
  };

  // Handle submit
  const handleSubmit = async () => {
    setThrowError(false);
    if (!validateInputs()) {
      showToast(t.acceptDeclarations, "error");
      return;
    }

    // If no new files to upload, just validate declarations
    // if (selectedFiles.length === 0) {
    //   showToast(t.uploadSuccess, "success");
    //   setTimeout(() => window.location.reload(), 2000);
    //   return;
    // }

    setLoading(true);
    try {
      const formData = new FormData();
      
      // Append all selected files
      selectedFiles.forEach((file) => {
        formData.append("file", file);
      });

      // Create a custom fetch for FormData - don't set Content-Type, browser will set it with boundary
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(
        `${USER_BASE_URL}/${VERSION}/complete-profile/step-3`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "app-version": "1.0.0",
            // Don't set Content-Type for FormData - browser sets it automatically with boundary
          },
          body: formData,
        }
      );

      const data = await response.json();

      setLoading(false);
      if (data && data.response_code === 200) {
        showToast(data.message || t.uploadSuccess, "success");
        setSelectedFiles([]);
        // Reload to get updated document list
        setTimeout(() => navigate("/profile-step-4"), 500);
      } else {
        const currentLang = localStorage.getItem("app_language") || "en";
        const translations = {
          en: { uploadFailed: "Failed to upload documents" },
          ar: { uploadFailed: "فشل تحميل المستندات" },
        };
        showToast(data.message || translations[currentLang].uploadFailed, "error");
      }
    } catch (err) {
      console.error(err);
      setLoading(false);
      showToast(err.message || t.uploadFailed, "error");
    }
  };

  if (initialLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  const totalFiles = existingDocuments.length + selectedFiles.length;
  const canUploadMore = totalFiles < MAX_FILES;

  return (
    <div className="common-wrapper" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Navbar is_back_arrow={true} is_step_heading={true} heading={t.step} step_number={3} />

      <h2 className="title">{t.companyDocuments}</h2>
      <p className="subtitle">{t.subtitle}</p>

      <form className="pc-form">
        {/* Establishment Documents */}
        <p className="form-field-title">{t.establishmentDocuments}</p>
        <p className="file-upload-info">{t.maxFilesInfo}</p>
        
        {/* Upload Area */}
        <div className="input-field-div">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.svg,.webp,.avif,.heic,.doc,.docx"
            onChange={handleFileSelect}
            disabled={!canUploadMore || loading}
            className="file-input-hidden"
          />
          
          <div
            onClick={() => canUploadMore && !loading && fileInputRef.current?.click()}
            className={`file-upload-area ${!canUploadMore || loading ? "disabled" : ""}`}
          >
            <p>{t.clickToUpload}</p>
          </div>

          {/* Existing Documents */}
          {existingDocuments.length > 0 && (
            <div className="file-list-container">
              <div className="file-list-grid">
                {existingDocuments.map((doc, index) => (
                  <div key={doc.id} className="file-item-wrapper">
                    <div
                      onClick={() => viewingId !== doc.id && handleViewDocument(doc.id)}
                      className={`file-icon file-icon-existing ${viewingId === doc.id ? "loading" : ""}`}
                    >
                      {viewingId === doc.id ? (
                        <div className="loader" />
                      ) : (
                        getFileExtension(doc).toUpperCase()
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => deletingId !== doc.id && handleDeleteDocument(doc.id)}
                      disabled={deletingId === doc.id || viewingId === doc.id}
                      className="file-delete-btn"
                      aria-label={t.deleteFile}
                    >
                      {deletingId === doc.id ? (
                        <div className="loader" />
                      ) : (
                        "×"
                      )}
                    </button>
                    <p className="file-name">
                      {getFileName(doc).length > 15
                        ? getFileName(doc).substring(0, 12) + "..."
                        : getFileName(doc)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Selected Files */}
          {selectedFiles.length > 0 && (
            <div className="file-list-container">
              <div className="file-list-grid">
                {selectedFiles.map((file, index) => (
                  <div key={index} className="file-item-wrapper">
                    <div className="file-icon file-icon-new">
                      {getFileExtension(file).toUpperCase()}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(index)}
                      className="file-delete-btn"
                      aria-label={t.deleteFile}
                    >
                      ×
                    </button>
                    <p className="file-name">
                      {file.name.length > 15 ? file.name.substring(0, 12) + "..." : file.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Declarations */}
        <div className="input-field-div declaration-container">
          <label className="declaration-label">
            <input
              type="checkbox"
              checked={declaration1}
              onChange={(e) => {
                setDeclaration1(e.target.checked);
                setTouched((t) => ({ ...t, declaration1: true }));
              }}
              className="declaration-checkbox"
            />
            <span className="declaration-text">
              {t.declaration1}{" "}
              <a
                href="https://g9eduinternational.com/terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                className="declaration-link"
              >
                {t.termsOfService}
              </a>{" "}
              {t.and}{" "}
              <a
                href="https://g9eduinternational.com/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                className="declaration-link"
              >
                {t.privacyPolicy}
              </a>
              .
            </span>
          </label>

          <label className="declaration-label">
            <input
              type="checkbox"
              checked={declaration2}
              onChange={(e) => {
                setDeclaration2(e.target.checked);
                setTouched((t) => ({ ...t, declaration2: true }));
              }}
              className="declaration-checkbox"
            />
            <span className="declaration-text">
              {t.declaration2}
            </span>
          </label>

          {(touched.declaration1 || touched.declaration2 || throwError) &&
            (!declaration1 || !declaration2) && (
              <div className="error">{t.acceptDeclarations}</div>
            )}
        </div>
      </form>

      <button
        onClick={handleSubmit}
        type="submit"
        className="continue-btn"
        disabled={loading}
      >
        {loading ? <span className="loader"></span> : t.continue}
      </button>
    </div>
  );
}

